import { useMemo, useState } from "react";
import { chargeDateKey, chargeMonthKey } from "../utils/chargeDates";
import { Link } from "react-router-dom";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import OutlinedStatusButton from "../../../DesignSystem/Buttons/OutlinedStatusButtons";
import Tooltip from "../../../DesignSystem/Tooltip/Tooltip";
import { ACTION_STATUSES, DEFAULT_ACTION_STATUS_LABEL, DEFAULT_ACTION_STATUS_ID } from "../constants/guidelineCharges";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import "../../../../css/Marketing/WebsiteCharges.css";
import "../../../../css/Finance/FlaggedCharges.css";
import { ReactComponent as HighVarianceIcon } from "../../../../assets/icons/finance/VarianceHigh.svg";
import { ReactComponent as LowVarianceIcon } from "../../../../assets/icons/finance/VarianceLow.svg";
import { ReactComponent as ChargeSourceIcon } from "../../../../assets/icons/finance/CrossLaunch.svg";
import {ReactComponent as InfoIcon} from "../../../../assets/icons/information.svg"
import {
  hasMissingAdviserEmail,
  requiresChargeReview,
} from "../services/chargeReviewService";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value,
  );
const monthKey = (row) => row.billingMonth || chargeMonthKey(row.date);

export const getFlaggedCharges = (records, month) =>
  records
    .filter(
      (row) =>
        monthKey(row) === month &&
        requiresChargeReview(row),
    )
    .map((row) => ({
      ...row,
      flagReason: hasMissingAdviserEmail(row)
        ? row.flagReason || "Missing adviser email"
        : row.flagReason,
      variance:
        Number.isFinite(row.amountCharged) && Number.isFinite(row.expectedAmount)
          ? (Math.round(row.amountCharged * 100) - Math.round(row.expectedAmount * 100)) / 100
          : 0,
    }));

const FlaggedChargesTable = ({
  records,
  selectedMonth,
  onTakeAction,
  includeAll = false,
  showVariance = !includeAll,
}) => {
  const [sort, setSort] = useState({ key: "date", direction: -1 });
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [saving, setSaving] = useState({});
  const statusOptions = ACTION_STATUSES.map((status) => ({
    id: status.id,
    name: status.label,
  }));
  const rows = useMemo(
    () =>
      (includeAll
        ? (records || []).filter((row) => monthKey(row) === selectedMonth).map((row) => ({ ...row, variance: (Math.round(row.amountCharged * 100) - Math.round(row.expectedAmount * 100)) / 100 }))
        : getFlaggedCharges(records || [], selectedMonth)
      ).sort(
        (a, b) =>
          String(a[sort.key] || "").localeCompare(String(b[sort.key] || "")) *
          sort.direction,
      ),
    [records, selectedMonth, sort, includeAll],
  );
  const sortBy = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));

  return rows.length ? (
    <section className="website-dashboard-card table-scroll-card">
      <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
        <table
          className="website-table flagged-charges-table"
          aria-label="Flagged charges"
        >
          <thead>
            <tr>
              <th
                className="FP_Help_Text_Semibold table-index-column"
                scope="col"
                aria-label="Row number"
              ></th>
              {[
                ["adviser", "Adviser"],
                ["date", "Date"],
                ["chargeType", "Charge Type"],
              ].map(([key, label]) => (
                <th
                  key={key}
                  className="FP_Help_Text_Semibold"
                  aria-sort={
                    sort.key === key
                      ? sort.direction === 1
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <button
                    type="button"
                    className="table-heading-content table-sort-button"
                    onClick={() => sortBy(key)}
                  >
                    {label}
                    <SortIcon aria-hidden />
                  </button>
                </th>
              ))}
              {[
                "Charge Description",
                "Client",
                "Amount",
                "Action Status",
                "Charge Source",
              ].map((label) => (
                <th
                  key={label}
                  className={`FP_Help_Text_Semibold ${label === "Amount" ? "text-align-right " : ""}`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={hasMissingAdviserEmail(row)
                    ? "monthly-payment-unlinked-email-row"
                    : ""}
                >
                  <td className="FP_Help_Text black table-index-column">
                    {index + 1}.
                  </td>
                  <td className="FP_Help_Text black">
                    {row.adviser || "-"}
                  </td>
                  <td className="FP_Help_Text black">
                    {chargeDateKey(row.date).split("-").reverse().join("/") || "-"}
                  </td>
                  <td className="FP_Help_Text black">
                    <div className="fit-column">
                      <span>{row.chargeType}</span>
                      {row.chargeType === "Paraplanning" && row.paraplanner ? (
                        <span className="FP_Help_Text monthly-charge-paraplanner">
                          {row.paraplanner}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="FP_Help_Text black">
                    {row.description || (row.expectedGuideline ? "" : "-")}
                    {row.expectedGuideline ? (
                      <div className="flagged-charge-guideline">
                        {row.expectedGuideline}
                      </div>
                    ) : null}
                    {row.flagReason ? <div>{row.flagReason}</div> : null}
                  </td>
                  <td className="FP_Help_Text black">{row.client || "-"}</td>
                  <td className="FP_Help_Text black">
                    <dl className="flagged-charge-amounts">
                      <dt>{row.amountLabel || "Amount Charged"}</dt>
                      <dd>{money(row.amountCharged)}</dd>
                      <dt>Expected Amount</dt>
                      <dd>{money(row.expectedAmount)}</dd>
                      {showVariance && <dt>Variance</dt>}
                      {showVariance && <dd className="FP_Help_Text_Semibold row align-center justify-flex-end">
                        {row.variance === 0 ? null : row.variance > 0 ? (
                          <HighVarianceIcon width={20} height={20} />
                        ) : (
                          <LowVarianceIcon width={20} height={20} />
                        )}
                        {money(Math.abs(row.variance))}
                      </dd>}
                    </dl>
                  </td>
                  <td>
                    <div className="row align-center gap-s">
                    {includeAll ? (
                      <OutlinedStatusButton
                        label={ACTION_STATUSES.find((status) => status.id === row.actionStatus || status.label === row.actionStatus)?.label || row.actionStatus}
                        color={ACTION_STATUSES.find((status) => status.id === row.actionStatus || status.label === row.actionStatus)?.color || "grey"}
                      />
                    ) : <CustomSelect
                      options={statusOptions}
                      value={selectedStatuses[`${selectedMonth}:${row.id}`] ||
                        ACTION_STATUSES.find((status) => status.id === row.actionStatus || status.label === row.actionStatus)?.id || DEFAULT_ACTION_STATUS_ID}
                      placeholder={DEFAULT_ACTION_STATUS_LABEL}
                      searchable={false}
                      clearable={false}
                      disabled={!!saving[row.id]}
                      onChange={async (event) => {
                        const status = event.target.value;
                        if (!onTakeAction) {
                          setSelectedStatuses((previous) => ({ ...previous, [`${selectedMonth}:${row.id}`]: status }));
                          return;
                        }
                        setSaving((previous) => ({ ...previous, [row.id]: true }));
                        try { await onTakeAction(row, status); } catch { /* The review hook displays the save error. */ }
                        finally { setSaving((previous) => ({ ...previous, [row.id]: false })); }
                      }}
                    />}
                    {includeAll && row.history?.length > 0 && (
                      <Tooltip position="left" content={row.history.map((event) => {
                        const label = ACTION_STATUSES.find((status) => status.id === event.status)?.label || event.status;
                        const date = event.changedAt?.toDate?.();
                        return `${label} - ${event.automatic ? "Automatically detected; recorded by " : "Changed by "}${event.changedByName || event.changedBy}${date ? ` - ${date.toLocaleString("en-GB", { timeZone: "Europe/London" })}` : ""}`;
                      }).join("\n")}>
                          <span tabIndex={0} aria-label="Status history">
                            <InfoIcon width={20} height={20}/>
                        </span>
                      </Tooltip>
                    )}
                    </div>
                  </td>
                  <td className="FP_Help_Text black">
                    {row.sourcePath?.startsWith("/") &&
                    !row.sourcePath.startsWith("//") ? (
                      <Link
                        className="table-row-action"
                        to={row.sourcePath}
                        aria-label={`View charge source for ${row.adviser || "charge"}`}
                      >
                        <ChargeSourceIcon />
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="FP_Content primary">
                  {records == null
                    ? "Charge source not connected yet. Flagged charges will appear once actual charges are matched to the guidelines."
                    : "No charge variances found for this month."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  ) : (
    <p className="FP_Content" role="status">
      No flags
    </p>
  );
};
export default FlaggedChargesTable;
